import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useMutation } from '@tanstack/react-query'
import { ApiError, changeAdminPassword } from '@/lib/api'
import { useAuth } from '@/lib/auth'

export function ChangePasswordModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const { logout } = useAuth()

  const change = useMutation({
    mutationFn: () =>
      changeAdminPassword({ current_password: current, new_password: next }),
    onSuccess: async () => {
      // JWT becomes invalid after password change (password_changed_at > iat).
      // Force re-login with the new password.
      await logout()
    },
    onError: (e: unknown) => {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Error'
      setError(msg)
    },
  })

  const handleClose = () => {
    setCurrent('')
    setNext('')
    setConfirm('')
    setError('')
    change.reset()
    onClose()
  }

  const tooShort = next.length > 0 && next.length < 12
  const mismatch = next.length > 0 && confirm.length > 0 && next !== confirm
  const disabled =
    !current || next.length < 12 || next !== confirm || change.isPending

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>
            After change you'll be automatically logged out — sign in again with the new password.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="current-password">Current password</Label>
            <Input
              id="current-password"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-password">New password (min 12 characters)</Label>
            <Input
              id="new-password"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          {tooShort && (
            <p className="text-sm text-destructive">
              Password must be at least 12 characters
            </p>
          )}
          {mismatch && (
            <p className="text-sm text-destructive">Passwords don't match</p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              disabled={disabled}
              onClick={() => {
                setError('')
                change.mutate()
              }}
            >
              {change.isPending ? 'Changing…' : 'Change'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
